// components/RequisitionFormModal.jsx

"use client"
import { useState, useEffect, useCallback, useMemo, Fragment, useRef } from "react"
import { useForm, Controller } from "react-hook-form"
import { Dialog, Transition } from "@headlessui/react"
import { FiX, FiLoader, FiCheck, FiAlertCircle, FiFileText, FiFolder, FiClipboard, FiDollarSign, FiHelpCircle, FiMapPin, FiCalendar, FiUser, FiUpload } from "react-icons/fi"
import Select from "react-select"
import { createRequisition, updateRequisition } from "../api/procurementApi"
import { fetchProjects } from "../api/projectApi"
import { toast } from "react-toastify"
import axios from "axios"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

// Cache configuration
const PROJECTS_CACHE_KEY = 'projects_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const uploadFileToS3 = async (file, requisitionId) => {
    try {
        const token = localStorage.getItem("token")
        const { data } = await axios.post(
            `${API_BASE_URL}/requisitions/${requisitionId}/generate-upload-url`,
            { filetype: file.type },
            { headers: { Authorization: `Bearer ${token}` } },
        )
        const { uploadUrl, fileUrl } = data
        await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type },
        })
        await axios.post(
            `${API_BASE_URL}/requisitions/${requisitionId}/save-files`,
            { fileUrl },
            { headers: { Authorization: `Bearer ${token}` } },
        )
        return fileUrl
    } catch (error) {
        console.error("Failed to upload file to S3:", error);
        throw error;
    }
}

const DEPARTMENT_OPTIONS = [
    { value: "operations", label: "Operations" },
    { value: "finance", label: "Finance" },
    { value: "hr", label: "HR" },
    { value: "it", label: "IT" },
    { value: "logistics", label: "Logistics" },
    { value: "admin", label: "Administration" },
]

const PROCUREMENT_TYPE_OPTIONS = [
    { value: "goods_services", label: "Goods & Services" },
    { value: "financial_request", label: "Financial Request" },
    { value: "inquiry_resolution", label: "Inquiry / Resolution" },
    { value: "travel", label: "Travel Request" },
    { value: "contract", label: "Contract Request" },
]

const PRIORITY_OPTIONS = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
]

const URGENCY_OPTIONS = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Normal" },
    { value: "high", label: "Urgent" },
]

const TRANSPORT_MODE_OPTIONS = [
    { value: "flight", label: "Flight" },
    { value: "bus", label: "Bus" },
    { value: "car", label: "Car" },
    { value: "train", label: "Train" },
    { value: "taxi", label: "Taxi" },
    { value: "other", label: "Other" },
]

const ACCOMMODATION_TYPE_OPTIONS = [
    { value: "hotel", label: "Hotel" },
    { value: "guesthouse", label: "Guest House" },
    { value: "hostel", label: "Hostel" },
    { value: "apartment", label: "Apartment" },
    { value: "none", label: "Not Required" },
]

const REQUISITION_EDITABLE_STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted (Awaiting Director Approval)" },
]

// Cache helper functions
const getCachedProjects = () => {
    try {
        const cached = localStorage.getItem(PROJECTS_CACHE_KEY);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            // Check if cache is still valid
            if (Date.now() - timestamp < CACHE_DURATION) {
                return data;
            }
        }
    } catch (error) {
        console.error('Error reading cache:', error);
    }
    return null;
};

const setCachedProjects = (data) => {
    try {
        localStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify({
            data,
            timestamp: Date.now()
        }));
    } catch (error) {
        console.error('Error setting cache:', error);
    }
};

// Validation helper functions
const validateField = (fieldName, value, rules) => {
    const errors = [];
    
    if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errors.push(`${fieldName} is required`);
    }
    
    if (rules.minLength && value && value.length < rules.minLength) {
        errors.push(`${fieldName} must be at least ${rules.minLength} characters`);
    }
    
    if (rules.maxLength && value && value.length > rules.maxLength) {
        errors.push(`${fieldName} must not exceed ${rules.maxLength} characters`);
    }
    
    if (rules.pattern && value && !rules.pattern.test(value)) {
        errors.push(rules.message || `${fieldName} format is invalid`);
    }
    
    if (rules.min && value && parseFloat(value) < rules.min) {
        errors.push(`${fieldName} must be at least ${rules.min}`);
    }
    
    if (rules.max && value && parseFloat(value) > rules.max) {
        errors.push(`${fieldName} must not exceed ${rules.max}`);
    }
    
    return errors;
};

const RequisitionFormModal = ({ isOpen, onClose, onSaveSuccess, requisitionToEdit }) => {
    const modalRef = useRef(null);
    const statusMessageRef = useRef(null);
    const {
        register,
        handleSubmit,
        control,
        reset,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm({
        mode: "onChange", // Validate on change for immediate feedback
        reValidateMode: "onChange", // Revalidate when values change
    })
    
    const [projectOptions, setProjectOptions] = useState([])
    const [loadingProjects, setLoadingProjects] = useState(false)
    const [attachmentFiles, setAttachmentFiles] = useState([])
    const [isUploading, setIsUploading] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [submissionStatus, setSubmissionStatus] = useState(null)
    const [submissionMessage, setSubmissionMessage] = useState("")
    const [fieldErrors, setFieldErrors] = useState({}) // Additional field errors
    const [expiredDueDateWarning, setExpiredDueDateWarning] = useState(false)
    const [displayBudgetCode, setDisplayBudgetCode] = useState("")


    const watchedTypeOfProcurement = watch("typeOfProcurement")
    const watchedLineItems = watch("lineItems")
    const isEditing = !!requisitionToEdit

    const isStatusEditable = useMemo(() => {
        return isEditing && ["draft", "rejected_by_director"].includes(requisitionToEdit.currentStatus)
    }, [isEditing, requisitionToEdit])

    // Optimized fetch function with caching
    const fetchProjectsWithCache = useCallback(async (forceRefresh = false) => {
        // Try to get from cache first
        if (!forceRefresh) {
            const cachedProjects = getCachedProjects();
            if (cachedProjects) {
                const options = cachedProjects.map((p) => ({ value: p._id, label: p.projectTitle, budgetCode: p.budgetCode }))
                setProjectOptions(options);
                return cachedProjects;
            }
        }

        setLoadingProjects(true);
        try {
            const projectsData = await fetchProjects();
            const options = projectsData.map((p) => ({ value: p._id, label: p.projectTitle, budgetCode: p.budgetCode }))
            setProjectOptions(options);
            
            // Cache the results
            setCachedProjects(projectsData);
            
            return projectsData;
        } catch (error) {
            console.error("Error fetching projects:", error);
            toast.error("Failed to load projects. Please try again.");
            throw error;
        } finally {
            setLoadingProjects(false);
        }
    }, []);

    // Prefetch projects on component mount
    useEffect(() => {
        fetchProjectsWithCache();
    }, [fetchProjectsWithCache]);

    // Fetch projects when modal opens (with cache)
    useEffect(() => {
        if (isOpen) {
            fetchProjectsWithCache();
        }
    }, [isOpen, fetchProjectsWithCache]);

    // Background refresh every 5 minutes when component is mounted
    useEffect(() => {
        const interval = setInterval(() => {
            fetchProjectsWithCache(true); // Force refresh
        }, CACHE_DURATION);

        return () => clearInterval(interval);
    }, [fetchProjectsWithCache]);

    // Check for expired due date when editing
    useEffect(() => {
        if (isEditing && isOpen && requisitionToEdit?.dueDate) {
            const dueDate = new Date(requisitionToEdit.dueDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (dueDate < today) {
                setExpiredDueDateWarning(true);
                toast.warning(
                    "This requisition has an expired due date. Please update the due date before submitting.",
                    { autoClose: 5000 }
                );
            } else {
                setExpiredDueDateWarning(false);
            }
        }
    }, [isEditing, isOpen, requisitionToEdit]);


    // Watch project selection to show budget code
    useEffect(() => {
        const selectedProjectValue = watch("projectId");
        
        if (selectedProjectValue && selectedProjectValue.value) {
            // Find the full project object from the options list
            const project = projectOptions.find(p => p.value === selectedProjectValue.value);
            
            if (project) {
                setDisplayBudgetCode(project.budgetCode || "Loading...");
            }
        } else {
            setDisplayBudgetCode("");
        }
    }, [watch("projectId"), projectOptions]);



    

    // Scroll to status message when it appears
    useEffect(() => {
        if (submissionStatus && statusMessageRef.current) {
            statusMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [submissionStatus]);

    useEffect(() => {
        if (isOpen && projectOptions.length > 0) {
            const defaultRequesterName = localStorage.getItem("userName") || localStorage.getItem("name") || "";
            const defaultRequesterDepartment = localStorage.getItem("department") || "";

            if (isEditing) {
                const initialProjectId = requisitionToEdit.projectId?._id || requisitionToEdit.projectId
                const defaultValues = {
                    ...requisitionToEdit,
                    projectId: projectOptions.find((opt) => opt.value === initialProjectId) || null,
                    departmentsTargeted: requisitionToEdit.departmentsTargeted
                        ? requisitionToEdit.departmentsTargeted.map((dept) => ({
                            value: dept,
                            label: DEPARTMENT_OPTIONS.find((opt) => opt.value === dept)?.label || dept,
                        }))
                        : [],
                    typeOfProcurement:
                        PROCUREMENT_TYPE_OPTIONS.find((opt) => opt.value === requisitionToEdit.typeOfProcurement) || null,
                    priority: PRIORITY_OPTIONS.find((opt) => opt.value === requisitionToEdit.priority) || null,
                    dueDate: requisitionToEdit.dueDate ? new Date(requisitionToEdit.dueDate).toISOString().split("T")[0] : "",
                    currentStatus: isStatusEditable
                        ? REQUISITION_EDITABLE_STATUS_OPTIONS.find((opt) => opt.value === requisitionToEdit.currentStatus) || null
                        : { value: requisitionToEdit.currentStatus, label: requisitionToEdit.currentStatus.replace(/_/g, " ") },
                    financialDetails: requisitionToEdit.financialDetails || { amount: "", currency: "USD", budgetCode: "" },
                    inquiryDetails: requisitionToEdit.inquiryDetails || { caseReference: "", resolutionNotes: "" },
                    lineItems:
                        requisitionToEdit.lineItems && requisitionToEdit.lineItems.length > 0
                            ? requisitionToEdit.lineItems
                            : [{ itemName: "", quantity: 1, unitPrice: "" }],
                    travelDetails: {
                        destination: requisitionToEdit.travelDetails?.destination || "",
                        purpose: requisitionToEdit.travelDetails?.purposeOfTravel || requisitionToEdit.travelDetails?.purpose || "",
                        startDate: requisitionToEdit.travelDetails?.startDate ? new Date(requisitionToEdit.travelDetails.startDate) : null,
                        endDate: requisitionToEdit.travelDetails?.endDate ? new Date(requisitionToEdit.travelDetails.endDate) : null,
                        estimatedCost: requisitionToEdit.travelDetails?.estimatedCost || "",
                        urgency: URGENCY_OPTIONS.find((opt) => opt.value === requisitionToEdit.travelDetails?.urgency) || URGENCY_OPTIONS[1],
                        transportMode: TRANSPORT_MODE_OPTIONS.find((opt) => opt.value === requisitionToEdit.travelDetails?.transportMode) || null,
                        accommodationType: ACCOMMODATION_TYPE_OPTIONS.find((opt) => opt.value === requisitionToEdit.travelDetails?.accommodationType) || null,
                        description: requisitionToEdit.travelDetails?.description || "",
                        requesterName: requisitionToEdit.travelDetails?.requesterName || defaultRequesterName,
                        department: requisitionToEdit.travelDetails?.department || defaultRequesterDepartment,
                    },
                }
                reset(defaultValues)
            } else {
                reset({
                    title: "",
                    description: "",
                    dueDate: "",
                    priority: PRIORITY_OPTIONS[1],
                    departmentsTargeted: [],
                    typeOfProcurement: PROCUREMENT_TYPE_OPTIONS[0],
                    lineItems: [{ itemName: "", quantity: 1, unitPrice: "" }],
                    financialDetails: { amount: "", currency: "USD", budgetCode: "" },
                    inquiryDetails: { caseReference: "", resolutionNotes: "" },
                    travelDetails: {
                        destination: "",
                        purpose: "",
                        startDate: null,
                        endDate: null,
                        estimatedCost: "",
                        urgency: URGENCY_OPTIONS[1],
                        transportMode: null,
                        accommodationType: null,
                        description: "",
                        requesterName: defaultRequesterName,
                        department: defaultRequesterDepartment,
                    },
                    projectId: null,
                    currentStatus: REQUISITION_EDITABLE_STATUS_OPTIONS[0],
                })
            }
        }
        setAttachmentFiles([])
        setIsSubmitted(false)
        setSubmissionStatus(null)
        setSubmissionMessage("")
        setFieldErrors({})
    }, [requisitionToEdit, reset, projectOptions, isOpen, isStatusEditable, isEditing])

    const handleAddLineItem = useCallback(() => {
        const currentLineItems = watchedLineItems || []
        setValue("lineItems", [...currentLineItems, { itemName: "", quantity: 1, unitPrice: "" }])
    }, [watchedLineItems, setValue])

    const handleRemoveLineItem = useCallback(
        (index) => {
            const currentLineItems = watchedLineItems
            setValue(
                "lineItems",
                currentLineItems.filter((_, i) => i !== index),
            )
        },
        [watchedLineItems, setValue],
    )

    const onFileChange = (e) => {
        const files = Array.from(e.target.files);
        const maxSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'image/jpeg', 'image/png'];
        
        const invalidFiles = files.filter(file => {
            if (file.size > maxSize) {
                toast.error(`${file.name} exceeds the 10MB size limit`);
                return true;
            }
            if (!allowedTypes.includes(file.type)) {
                toast.error(`${file.name} is not a supported file type`);
                return true;
            }
            return false;
        });
        
        if (invalidFiles.length === 0) {
            setAttachmentFiles(files);
        } else {
            // Filter out invalid files
            setAttachmentFiles(files.filter(file => !invalidFiles.includes(file)));
        }
    }

    // Custom validation function
    const validateForm = (formData) => {
        const errors = {};
        
        // Title validation
        const titleErrors = validateField("Title", formData.title, {
            required: true,
            minLength: 5,
            maxLength: 100
        });
        if (titleErrors.length > 0) errors.title = titleErrors;
        
        // Description validation
        const descriptionErrors = validateField("Description", formData.description, {
            required: true,
            minLength: 10,
            maxLength: 1000
        });
        if (descriptionErrors.length > 0) errors.description = descriptionErrors;
        
        // Due date validation
        if (!formData.dueDate) {
            errors.dueDate = ["Due date is required"];
        } else {
            const dueDate = new Date(formData.dueDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (dueDate < today) {
                errors.dueDate = ["Due date cannot be in the past"];
            }
        }
        
        // Priority validation
        if (!formData.priority || !formData.priority.value) {
            errors.priority = ["Priority is required"];
        }
        
        // Departments validation
        if (!formData.departmentsTargeted || formData.departmentsTargeted.length === 0) {
            errors.departmentsTargeted = ["At least one department must be selected"];
        }
        
        // Type of procurement validation
        if (!formData.typeOfProcurement || !formData.typeOfProcurement.value) {
            errors.typeOfProcurement = ["Procurement type is required"];
        }
        
        // Dynamic validation based on procurement type
        if (formData.typeOfProcurement?.value === "goods_services") {
            if (!formData.lineItems || formData.lineItems.length === 0) {
                errors.lineItems = ["At least one item is required"];
            } else {
                const lineItemErrors = [];
                formData.lineItems.forEach((item, index) => {
                    const itemErrors = {};
                    
                    const itemNameErrors = validateField("Item name", item.itemName, {
                        required: true,
                        minLength: 3,
                        maxLength: 100
                    });
                    if (itemNameErrors.length > 0) itemErrors.itemName = itemNameErrors;
                    
                    const quantityErrors = validateField("Quantity", item.quantity, {
                        required: true,
                        min: 1,
                        max: 9999
                    });
                    if (quantityErrors.length > 0) itemErrors.quantity = quantityErrors;
                    
                    const unitPriceErrors = validateField("Unit price", item.unitPrice, {
                        min: 0,
                        max: 999999
                    });
                    if (unitPriceErrors.length > 0) itemErrors.unitPrice = unitPriceErrors;
                    
                    if (Object.keys(itemErrors).length > 0) {
                        lineItemErrors[index] = itemErrors;
                    }
                });
                
                if (lineItemErrors.length > 0) {
                    errors.lineItems = lineItemErrors;
                }
            }
        }
        
        if (formData.typeOfProcurement?.value === "financial_request") {
            const amountErrors = validateField("Amount", formData.financialDetails?.amount, {
                required: true,
                min: 0.01,
                max: 999999
            });
            if (amountErrors.length > 0) errors["financialDetails.amount"] = amountErrors;
            
            const currencyErrors = validateField("Currency", formData.financialDetails?.currency, {
                required: true,
                minLength: 3,
                maxLength: 3,
                pattern: /^[A-Z]{3}$/
            });
            if (currencyErrors.length > 0) errors["financialDetails.currency"] = currencyErrors;
            
            const budgetCodeErrors = validateField("Budget code", formData.financialDetails?.budgetCode, {
                maxLength: 20
            });
            if (budgetCodeErrors.length > 0) errors["financialDetails.budgetCode"] = budgetCodeErrors;
        }
        
        if (formData.typeOfProcurement?.value === "inquiry_resolution") {
            const caseRefErrors = validateField("Case reference", formData.inquiryDetails?.caseReference, {
                required: true,
                minLength: 3,
                maxLength: 50
            });
            if (caseRefErrors.length > 0) errors["inquiryDetails.caseReference"] = caseRefErrors;
            
            const resolutionErrors = validateField("Resolution notes", formData.inquiryDetails?.resolutionNotes, {
                required: true,
                minLength: 10,
                maxLength: 1000
            });
            if (resolutionErrors.length > 0) errors["inquiryDetails.resolutionNotes"] = resolutionErrors;
        }
        
        if (formData.typeOfProcurement?.value === "travel") {
            const nameErrors = validateField("Requester name", formData.travelDetails?.requesterName, {
                required: true,
                minLength: 3,
                maxLength: 100
            });
            if (nameErrors.length > 0) errors["travelDetails.requesterName"] = nameErrors;
            
            const destinationErrors = validateField("Destination", formData.travelDetails?.destination, {
                required: true,
                minLength: 3,
                maxLength: 100
            });
            if (destinationErrors.length > 0) errors["travelDetails.destination"] = destinationErrors;
            
            const purposeErrors = validateField("Purpose of travel", formData.travelDetails?.purpose, {
                required: true,
                minLength: 5,
                maxLength: 200
            });
            if (purposeErrors.length > 0) errors["travelDetails.purpose"] = purposeErrors;
            
            if (!formData.travelDetails?.startDate) {
                errors["travelDetails.startDate"] = ["Start date is required"];
            }
            
            if (!formData.travelDetails?.endDate) {
                errors["travelDetails.endDate"] = ["End date is required"];
            }
            
            if (formData.travelDetails?.startDate && formData.travelDetails?.endDate) {
                const startDate = new Date(formData.travelDetails.startDate);
                const endDate = new Date(formData.travelDetails.endDate);
                
                if (startDate > endDate) {
                    errors["travelDetails.endDate"] = ["End date must be after start date"];
                }
            }
            
            const costErrors = validateField("Estimated cost", formData.travelDetails?.estimatedCost, {
                required: true,
                min: 0.01,
                max: 999999
            });
            if (costErrors.length > 0) errors["travelDetails.estimatedCost"] = costErrors;
        }
        
        return errors;
    }

    const onSubmit = async (formData) => {
        setSubmissionStatus(null)
        setSubmissionMessage("")
        setFieldErrors({})
        
        // Custom validation
        const validationErrors = validateForm(formData);
        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors);
            
            // Set errors in react-hook-form as well
            Object.keys(validationErrors).forEach(key => {
                if (key.includes('.')) {
                    const [parent, child] = key.split('.');
                    if (parent === 'lineItems') {
                        // Handle line items errors
                        Object.keys(validationErrors[key]).forEach(index => {
                            Object.keys(validationErrors[key][index]).forEach(field => {
                                setError(`lineItems.${index}.${field}`, {
                                    type: 'manual',
                                    message: validationErrors[key][index][field][0]
                                });
                            });
                        });
                    } else {
                        setError(key, {
                            type: 'manual',
                            message: validationErrors[key][0]
                        });
                    }
                } else {
                    setError(key, {
                        type: 'manual',
                        message: validationErrors[key][0]
                    });
                }
            });
            
            toast.error("Please fix the errors in the form before submitting.");
            return;
        }
        
        try {
            let requisitionResponse;

            if (isEditing && requisitionToEdit?._id) {
                const payload = {
                    ...formData,
                    priority: formData.priority?.value,
                    typeOfProcurement: formData.typeOfProcurement?.value,
                    departmentsTargeted: formData.departmentsTargeted?.map((d) => d.value) || [],
                    projectId: formData.projectId?.value || formData.projectId || null,
                    currentStatus: formData.currentStatus?.value || "draft",
                }
                if (payload.typeOfProcurement === "travel" && payload.travelDetails) {
                    payload.travelDetails = {
                        ...payload.travelDetails,
                        urgency: payload.travelDetails?.urgency?.value,
                        transportMode: payload.travelDetails?.transportMode?.value,
                        accommodationType: payload.travelDetails?.accommodationType?.value,
                        startDate: payload.travelDetails?.startDate,
                        endDate: payload.travelDetails?.endDate,
                        estimatedCost: Number.parseFloat(payload.travelDetails.estimatedCost) || 0,
                    }
                    payload.travelDetails.purposeOfTravel = payload.travelDetails.purpose
                    payload.travelDetails.additionalDescription = payload.travelDetails.description
                }
                if (payload.typeOfProcurement !== "goods_services") delete payload.lineItems
                if (payload.typeOfProcurement !== "financial_request") delete payload.financialDetails
                if (payload.typeOfProcurement !== "inquiry_resolution") delete payload.inquiryDetails
                if (payload.typeOfProcurement !== "travel") delete payload.travelDetails

                requisitionResponse = await updateRequisition(requisitionToEdit._id, payload)
                setSubmissionStatus("success")
                setSubmissionMessage("Requisition updated successfully!")
            } else {
                const payload = {
                    ...formData,
                    priority: formData.priority?.value,
                    typeOfProcurement: formData.typeOfProcurement?.value,
                    departmentsTargeted: formData.departmentsTargeted?.map((d) => d.value) || [],
                    projectId: formData.projectId?.value || formData.projectId || null,
                    currentStatus: formData.currentStatus?.value || "draft",
                }
                if (payload.typeOfProcurement === "travel" && payload.travelDetails) {
                    payload.travelDetails = {
                        ...payload.travelDetails,
                        urgency: payload.travelDetails?.urgency?.value,
                        transportMode: payload.travelDetails?.transportMode?.value,
                        accommodationType: payload.travelDetails?.accommodationType?.value,
                        startDate: payload.travelDetails?.startDate,
                        endDate: payload.travelDetails?.endDate,
                        estimatedCost: Number.parseFloat(payload.travelDetails.estimatedCost) || 0,
                    }
                    payload.travelDetails.purposeOfTravel = payload.travelDetails.purpose
                    payload.travelDetails.additionalDescription = payload.travelDetails.description
                }
                if (payload.typeOfProcurement !== "goods_services") delete payload.lineItems
                if (payload.typeOfProcurement !== "financial_request") delete payload.financialDetails
                if (payload.typeOfProcurement !== "inquiry_resolution") delete payload.inquiryDetails
                if (payload.typeOfProcurement !== "travel") delete payload.travelDetails

                requisitionResponse = await createRequisition(payload)
                setSubmissionStatus("success")
                setSubmissionMessage("Requisition created successfully!")
            }

            setIsSubmitted(true)

            // Handle file uploads after successful requisition creation/update
            if (attachmentFiles.length > 0 && requisitionResponse?.data?.data?._id) {
                setIsUploading(true);
                const requisitionId = requisitionResponse.data.data._id;
                let uploadErrors = [];

                try {
                    for (const file of attachmentFiles) {
                        try {
                            await uploadFileToS3(file, requisitionId);
                        } catch (error) {
                            uploadErrors.push(file.name);
                        }
                    }
                    
                    if (uploadErrors.length === 0) {
                        setSubmissionStatus("success")
                        setSubmissionMessage(prev => prev + " All attachments uploaded successfully!")
                    } else {
                        setSubmissionStatus("error")
                        setSubmissionMessage(`Requisition saved but failed to upload: ${uploadErrors.join(', ')}`)
                    }
                } finally {
                    setIsUploading(false);
                }
            }

            // Show success toast
            toast.success(submissionMessage || "Operation completed successfully!")
            
            // Close modal after a short delay to show the success message
            setTimeout(() => {
                onSaveSuccess?.(requisitionResponse?.data?.data)
                onClose()
            }, 2000)
            
        } catch (err) {
            console.error("Error creating/updating requisition:", err)
            const errorMessage = err.response?.data?.message || err.message || "Failed to save requisition. Please try again."
            setSubmissionStatus("error")
            setSubmissionMessage(errorMessage)
            toast.error(errorMessage)
        }
    }

    // Helper function to get error message for a field
    const getErrorMessage = (fieldName) => {
        if (errors[fieldName]) {
            return errors[fieldName].message;
        }
        if (fieldErrors[fieldName]) {
            return Array.isArray(fieldErrors[fieldName]) 
                ? fieldErrors[fieldName][0] 
                : fieldErrors[fieldName];
        }
        return null;
    }

    // Helper function to get error message for nested fields
    const getNestedErrorMessage = (parentField, childField, index = null) => {
        const key = index !== null 
            ? `${parentField}.${index}.${childField}`
            : `${parentField}.${childField}`;
            
        if (errors[key]) {
            return errors[key].message;
        }
        if (fieldErrors[parentField] && index !== null && fieldErrors[parentField][index] && fieldErrors[parentField][index][childField]) {
            return Array.isArray(fieldErrors[parentField][index][childField])
                ? fieldErrors[parentField][index][childField][0]
                : fieldErrors[parentField][index][childField];
        }
        if (fieldErrors[key]) {
            return Array.isArray(fieldErrors[key])
                ? fieldErrors[key][0]
                : fieldErrors[key];
        }
        return null;
    }

    // Function to clear error for a specific field
    const clearFieldError = (fieldName) => {
        clearErrors(fieldName);
        setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    }

    // Function to clear error for nested fields
    const clearNestedFieldError = (parentField, childField, index = null) => {
        const key = index !== null 
            ? `${parentField}.${index}.${childField}`
            : `${parentField}.${childField}`;
            
        clearErrors(key);
        setFieldErrors(prev => {
            const newErrors = { ...prev };
            if (index !== null && newErrors[parentField] && newErrors[parentField][index]) {
                delete newErrors[parentField][index][childField];
                // Clean up empty objects
                if (Object.keys(newErrors[parentField][index]).length === 0) {
                    delete newErrors[parentField][index];
                }
                if (Object.keys(newErrors[parentField]).length === 0) {
                    delete newErrors[parentField];
                }
            } else {
                delete newErrors[key];
            }
            return newErrors;
        });
    }

    // Watch for changes to clear errors
    const watchedTitle = watch("title");
    const watchedDescription = watch("description");
    const watchedDueDate = watch("dueDate");
    const watchedPriority = watch("priority");
    const watchedDepartmentsTargeted = watch("departmentsTargeted");
    const watchedCurrentStatus = watch("currentStatus");

    // Clear errors when fields are corrected
    useEffect(() => {
        if (watchedTitle && watchedTitle.length >= 5) {
            clearFieldError("title");
        }
    }, [watchedTitle]);

    useEffect(() => {
        if (watchedDescription && watchedDescription.length >= 10) {
            clearFieldError("description");
        }
    }, [watchedDescription]);

    useEffect(() => {
        if (watchedDueDate) {
            const dueDate = new Date(watchedDueDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (dueDate >= today) {
                clearFieldError("dueDate");
                // Clear expired due date warning if updated
                if (expiredDueDateWarning) {
                    setExpiredDueDateWarning(false);
                }
            }
        }
    }, [watchedDueDate, expiredDueDateWarning]);

    useEffect(() => {
        if (watchedPriority && watchedPriority.value) {
            clearFieldError("priority");
        }
    }, [watchedPriority]);

    useEffect(() => {
        if (watchedDepartmentsTargeted && watchedDepartmentsTargeted.length > 0) {
            clearFieldError("departmentsTargeted");
        }
    }, [watchedDepartmentsTargeted]);

    useEffect(() => {
        if (watchedTypeOfProcurement && watchedTypeOfProcurement.value) {
            clearFieldError("typeOfProcurement");
        }
    }, [watchedTypeOfProcurement]);

    useEffect(() => {
        if (watchedCurrentStatus && watchedCurrentStatus.value) {
            clearFieldError("currentStatus");
        }
    }, [watchedCurrentStatus]);

    // Watch for nested field changes
    const watchedFinancialDetails = watch("financialDetails");
    const watchedInquiryDetails = watch("inquiryDetails");
    const watchedTravelDetails = watch("travelDetails");

    useEffect(() => {
        if (watchedFinancialDetails?.amount && parseFloat(watchedFinancialDetails.amount) >= 0.01) {
            clearNestedFieldError("financialDetails", "amount");
        }
        if (watchedFinancialDetails?.currency && /^[A-Z]{3}$/.test(watchedFinancialDetails.currency)) {
            clearNestedFieldError("financialDetails", "currency");
        }
        if (watchedFinancialDetails?.budgetCode && watchedFinancialDetails.budgetCode.length <= 20) {
            clearNestedFieldError("financialDetails", "budgetCode");
        }
    }, [watchedFinancialDetails]);

    useEffect(() => {
        if (watchedInquiryDetails?.caseReference && watchedInquiryDetails.caseReference.length >= 3) {
            clearNestedFieldError("inquiryDetails", "caseReference");
        }
        if (watchedInquiryDetails?.resolutionNotes && watchedInquiryDetails.resolutionNotes.length >= 10) {
            clearNestedFieldError("inquiryDetails", "resolutionNotes");
        }
    }, [watchedInquiryDetails]);

    useEffect(() => {
        if (watchedTravelDetails?.requesterName && watchedTravelDetails.requesterName.length >= 3) {
            clearNestedFieldError("travelDetails", "requesterName");
        }
        if (watchedTravelDetails?.destination && watchedTravelDetails.destination.length >= 3) {
            clearNestedFieldError("travelDetails", "destination");
        }
        if (watchedTravelDetails?.purpose && watchedTravelDetails.purpose.length >= 5) {
            clearNestedFieldError("travelDetails", "purpose");
        }
        if (watchedTravelDetails?.startDate) {
            const startDate = new Date(watchedTravelDetails.startDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (startDate >= today) {
                clearNestedFieldError("travelDetails", "startDate");
            }
        }
        if (watchedTravelDetails?.endDate && watchedTravelDetails?.startDate) {
            const startDate = new Date(watchedTravelDetails.startDate);
            const endDate = new Date(watchedTravelDetails.endDate);
            if (endDate >= startDate) {
                clearNestedFieldError("travelDetails", "endDate");
            }
        }
        if (watchedTravelDetails?.estimatedCost && parseFloat(watchedTravelDetails.estimatedCost) >= 0.01) {
            clearNestedFieldError("travelDetails", "estimatedCost");
        }
    }, [watchedTravelDetails]);

    // Watch line items for changes
    useEffect(() => {
        if (watchedLineItems) {
            watchedLineItems.forEach((item, index) => {
                if (item.itemName && item.itemName.length >= 3) {
                    clearNestedFieldError("lineItems", "itemName", index);
                }
                if (item.quantity && parseFloat(item.quantity) >= 1) {
                    clearNestedFieldError("lineItems", "quantity", index);
                }
                if (item.unitPrice !== undefined && parseFloat(item.unitPrice) >= 0) {
                    clearNestedFieldError("lineItems", "unitPrice", index);
                }
            });
        }
    }, [watchedLineItems]);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-90 scale-95"
                        >
                            <Dialog.Panel 
                                ref={modalRef}
                                className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all max-h-[90vh] overflow-y-auto"
                            >
                                {/* Header with Orange Theme */}
                                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500">
                                    <div className="flex justify-between items-center">
                                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                                <FiClipboard className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                                            </div>
                                            <div>
                                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                                    {isEditing ? "Edit Requisition" : "Create New Requisition"}
                                                </h2>
                                                <div className="flex items-center mt-1 text-black text-sm">
                                                    <FiFolder className="w-3 h-3 mr-1" />
                                                    <span>Programs Department</span>
                                                </div>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={onClose} 
                                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                                        >
                                            <FiX size={20} />
                                        </button>
                                    </div>
                                </div>

                                {/* Modal Body */}
                                <div className="p-4 sm:p-6">
                                    {/* Expired Due Date Warning */}
                                    {expiredDueDateWarning && (
                                        <div className="mb-4 p-4 rounded-md bg-amber-50 border border-amber-200 flex items-start">
                                            <FiAlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                                            <div>
                                                <h3 className="text-sm font-medium text-amber-800">Expired Due Date</h3>
                                                <p className="text-sm text-amber-700 mt-1">
                                                    This requisition has an expired due date. Please update the due date to a future date before submitting.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Status Message */}
                                    {submissionStatus && (
                                        <div 
                                            ref={statusMessageRef}
                                            className={`mb-4 p-4 rounded-md flex items-center ${
                                                submissionStatus === "success" 
                                                    ? "bg-green-50 text-green-800" 
                                                    : "bg-red-50 text-red-800"
                                            }`}
                                        >
                                            {submissionStatus === "success" ? (
                                                <FiCheck className="h-5 w-5 mr-2" />
                                            ) : (
                                                <FiAlertCircle className="h-5 w-5 mr-2" />
                                            )}
                                            <span>{submissionMessage}</span>
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                        {/* Basic Requisition Details */}
                                        <div>
                                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                                                Title <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <FiFileText className="h-5 w-5 text-gray-400" />
                                                </div>
                                                <input
                                                    type="text"
                                                    id="title"
                                                    {...register("title", { 
                                                        required: "Title is required",
                                                        minLength: {
                                                            value: 5,
                                                            message: "Title must be at least 5 characters"
                                                        },
                                                        maxLength: {
                                                            value: 100,
                                                            message: "Title must not exceed 100 characters"
                                                        }
                                                    })}
                                                    onChange={(e) => {
                                                        setValue("title", e.target.value);
                                                        if (e.target.value.length >= 5) {
                                                            clearFieldError("title");
                                                        }
                                                    }}
                                                    className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${
                                                        getErrorMessage("title") 
                                                            ? "border-red-500 focus:ring-red-500" 
                                                            : "border-gray-300 focus:ring-orange-500"
                                                    } focus:outline-none focus:ring-2`}
                                                    placeholder="Enter requisition title"
                                                />
                                            </div>
                                            {getErrorMessage("title") && (
                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                    {getErrorMessage("title")}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                                Description <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                                    <FiFileText className="h-5 w-5 text-gray-400" />
                                                </div>
                                                <textarea
                                                    id="description"
                                                    {...register("description", { 
                                                        required: "Description is required",
                                                        minLength: {
                                                            value: 10,
                                                            message: "Description must be at least 10 characters"
                                                        },
                                                        maxLength: {
                                                            value: 1000,
                                                            message: "Description must not exceed 1000 characters"
                                                        }
                                                    })}
                                                    onChange={(e) => {
                                                        setValue("description", e.target.value);
                                                        if (e.target.value.length >= 10) {
                                                            clearFieldError("description");
                                                        }
                                                    }}
                                                    rows="3"
                                                    className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${
                                                        getErrorMessage("description") 
                                                            ? "border-red-500 focus:ring-red-500" 
                                                            : "border-gray-300 focus:ring-orange-500"
                                                    } focus:outline-none focus:ring-2 resize-none`}
                                                    placeholder="Enter requisition description"
                                                ></textarea>
                                            </div>
                                            {getErrorMessage("description") && (
                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                    {getErrorMessage("description")}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-2">
                                                Select Project
                                            </label>
                                            <Controller
                                                name="projectId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={projectOptions}
                                                        isLoading={loadingProjects}
                                                        className="mt-1"
                                                        placeholder="Select a project (Optional)"
                                                        isClearable={false}
                                                        onChange={(value) => {
                                                            field.onChange(value);
                                                            clearFieldError("projectId");
                                                        }}
                                                        styles={{
                                                            control: (base, state) => ({
                                                                ...base,
                                                                borderRadius: '0.75rem',
                                                                minHeight: '44px',
                                                                borderColor: getErrorMessage("projectId") ? '#EF4444' : (state.isFocused ? '#F97316' : '#D1D5DB'),
                                                                boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                '&:hover': {
                                                                    borderColor: getErrorMessage("projectId") ? '#EF4444' : '#F97316'
                                                                }
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                color: state.isSelected ? 'white' : '#1F2937',
                                                                '&:hover': {
                                                                    backgroundColor: '#FFEDD5'
                                                                }
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            />
                                                                                        {/* ✨ NEW: Display Budget Code when project is selected */}
                                            {displayBudgetCode && (
                                                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-md flex items-center justify-between shadow-sm">
                                                    <div className="flex items-center">
                                                        <div className="p-2 bg-orange-100 rounded-full mr-3">
                                                            <FiDollarSign className="text-orange-600 h-5 w-5" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Funds will be tagged to</p>
                                                            <p className="text-lg font-bold text-gray-800">{displayBudgetCode}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {getErrorMessage("projectId") && (
                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                    {getErrorMessage("projectId")}
                                                </p>
                                            )}
                                        </div>

                                        

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Due Date <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiCalendar className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="date"
                                                        id="dueDate"
                                                        {...register("dueDate", { 
                                                            required: "Due date is required",
                                                            validate: value => {
                                                                const dueDate = new Date(value);
                                                                const today = new Date();
                                                                today.setHours(0, 0, 0, 0);
                                                                if (dueDate < today) {
                                                                    return "Due date cannot be in the past";
                                                                }
                                                                return true;
                                                            }
                                                        })}
                                                        onChange={(e) => {
                                                            setValue("dueDate", e.target.value);
                                                            if (e.target.value) {
                                                                const dueDate = new Date(e.target.value);
                                                                const today = new Date();
                                                                today.setHours(0, 0, 0, 0);
                                                                if (dueDate >= today) {
                                                                    clearFieldError("dueDate");
                                                                }
                                                            }
                                                        }}
                                                        className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${
                                                            getErrorMessage("dueDate") 
                                                                ? "border-red-500 focus:ring-red-500" 
                                                                : "border-gray-300 focus:ring-orange-500"
                                                        } focus:outline-none focus:ring-2`}
                                                    />
                                                </div>
                                                {getErrorMessage("dueDate") && (
                                                    <p className="mt-1 text-xs text-red-600 flex items-center">
                                                        <FiAlertCircle className="h-3 w-3 mr-1" />
                                                        {getErrorMessage("dueDate")}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Priority <span className="text-red-500">*</span>
                                                </label>
                                                <Controller
                                                    name="priority"
                                                    control={control}
                                                    rules={{ required: "Priority is required" }}
                                                    render={({ field }) => (
                                                        <Select
                                                            {...field}
                                                            options={PRIORITY_OPTIONS}
                                                            className="mt-1"
                                                            placeholder="Select Priority"
                                                            isClearable={false}
                                                            onChange={(value) => {
                                                                field.onChange(value);
                                                                clearFieldError("priority");
                                                            }}
                                                            styles={{
                                                                control: (base, state) => ({
                                                                    ...base,
                                                                    borderRadius: '0.75rem',
                                                                    minHeight: '44px',
                                                                    borderColor: getErrorMessage("priority") ? '#EF4444' : (state.isFocused ? '#F97316' : '#D1D5DB'),
                                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                    '&:hover': {
                                                                        borderColor: getErrorMessage("priority") ? '#EF4444' : '#F97316'
                                                                    }
                                                                }),
                                                                option: (base, state) => ({
                                                                    ...base,
                                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                                    '&:hover': {
                                                                        backgroundColor: '#FFEDD5'
                                                                    }
                                                                }),
                                                            }}
                                                        />
                                                    )}
                                                />
                                                {getErrorMessage("priority") && (
                                                    <p className="mt-1 text-xs text-red-600 flex items-center">
                                                        <FiAlertCircle className="h-3 w-3 mr-1" />
                                                        {getErrorMessage("priority")}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="departmentsTargeted" className="block text-sm font-medium text-gray-700 mb-2">
                                                Target Departments <span className="text-red-500">*</span>
                                            </label>
                                            <Controller
                                                name="departmentsTargeted"
                                                control={control}
                                                rules={{ required: "At least one department must be selected" }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={DEPARTMENT_OPTIONS}
                                                        isMulti
                                                        className="mt-1"
                                                        placeholder="Select departments"
                                                        onChange={(value) => {
                                                            field.onChange(value);
                                                            if (value && value.length > 0) {
                                                                clearFieldError("departmentsTargeted");
                                                            }
                                                        }}
                                                        styles={{
                                                            control: (base, state) => ({
                                                                ...base,
                                                                borderRadius: '0.75rem',
                                                                minHeight: '44px',
                                                                borderColor: getErrorMessage("departmentsTargeted") ? '#EF4444' : (state.isFocused ? '#F97316' : '#D1D5DB'),
                                                                boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                '&:hover': {
                                                                    borderColor: getErrorMessage("departmentsTargeted") ? '#EF4444' : '#F97316'
                                                                }
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                color: state.isSelected ? 'white' : '#1F2937',
                                                                '&:hover': {
                                                                    backgroundColor: '#FFEDD5'
                                                                }
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            />
                                            {getErrorMessage("departmentsTargeted") && (
                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                    {getErrorMessage("departmentsTargeted")}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="typeOfProcurement" className="block text-sm font-medium text-gray-700 mb-2">
                                                Type of Procurement <span className="text-red-500">*</span>
                                            </label>
                                            <Controller
                                                name="typeOfProcurement"
                                                control={control}
                                                rules={{ required: "Procurement type is required" }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={PROCUREMENT_TYPE_OPTIONS}
                                                        className="mt-1"
                                                        placeholder="Select procurement type"
                                                        isClearable={false}
                                                        onChange={(value) => {
                                                            field.onChange(value);
                                                            clearFieldError("typeOfProcurement");
                                                        }}
                                                        styles={{
                                                            control: (base, state) => ({
                                                                ...base,
                                                                borderRadius: '0.75rem',
                                                                minHeight: '44px',
                                                                borderColor: getErrorMessage("typeOfProcurement") ? '#EF4444' : (state.isFocused ? '#F97316' : '#D1D5DB'),
                                                                boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                '&:hover': {
                                                                    borderColor: getErrorMessage("typeOfProcurement") ? '#EF4444' : '#F97316'
                                                                }
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                color: state.isSelected ? 'white' : '#1F2937',
                                                                '&:hover': {
                                                                    backgroundColor: '#FFEDD5'
                                                                }
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            />
                                            {getErrorMessage("typeOfProcurement") && (
                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                    {getErrorMessage("typeOfProcurement")}
                                                </p>
                                            )}
                                        </div>

                                        {isEditing && (
                                            <div>
                                                <label htmlFor="currentStatus" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Current Status <span className="text-red-500">*</span>
                                                </label>
                                                <Controller
                                                    name="currentStatus"
                                                    control={control}
                                                    rules={{ required: "Status is required" }}
                                                    render={({ field }) => (
                                                        <Select
                                                            {...field}
                                                            options={REQUISITION_EDITABLE_STATUS_OPTIONS}
                                                            className="mt-1"
                                                            placeholder="Select Status"
                                                            isClearable={false}
                                                            isDisabled={!isStatusEditable}
                                                            onChange={(value) => {
                                                                field.onChange(value);
                                                                clearFieldError("currentStatus");
                                                            }}
                                                            styles={{
                                                                control: (base, state) => ({
                                                                    ...base,
                                                                    borderRadius: '0.75rem',
                                                                    minHeight: '44px',
                                                                    borderColor: getErrorMessage("currentStatus") ? '#EF4444' : (state.isFocused ? '#F97316' : '#D1D5DB'),
                                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                    '&:hover': {
                                                                        borderColor: getErrorMessage("currentStatus") ? '#EF4444' : '#F97316'
                                                                    }
                                                                }),
                                                                option: (base, state) => ({
                                                                    ...base,
                                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                                    '&:hover': {
                                                                        backgroundColor: '#FFEDD5'
                                                                    }
                                                                }),
                                                            }}
                                                        />
                                                    )}
                                                />
                                                {!isStatusEditable && (
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Status cannot be changed when requisition is in '
                                                        {requisitionToEdit.currentStatus.replace(/_/g, " ")}'.
                                                    </p>
                                                )}
                                                {getErrorMessage("currentStatus") && (
                                                    <p className="mt-1 text-xs text-red-600 flex items-center">
                                                        <FiAlertCircle className="h-3 w-3 mr-1" />
                                                        {getErrorMessage("currentStatus")}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Dynamic Fields based on Type of Procurement */}
                                        {watchedTypeOfProcurement?.value === "goods_services" && (
                                            <div className="border border-dashed border-gray-300 p-4 rounded-md bg-gray-50">
                                                <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                                    <FiFileText className="mr-2 text-orange-500" />
                                                    Goods/Services Details
                                                </h4>
                                                {(watchedLineItems || []).map((item, index) => (
                                                    <div
                                                        key={index}
                                                        className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-3 p-2 border-b border-gray-200 last:border-b-0"
                                                    >
                                                        <div className="md:col-span-2">
                                                            <label
                                                                htmlFor={`lineItems.${index}.itemName`}
                                                                className="block text-xs font-medium text-gray-600"
                                                            >
                                                                Item Name <span className="text-red-500">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                id={`lineItems.${index}.itemName`}
                                                                {...register(`lineItems.${index}.itemName`, { 
                                                                    required: "Item name is required",
                                                                    minLength: {
                                                                        value: 3,
                                                                        message: "Item name must be at least 3 characters"
                                                                    },
                                                                    maxLength: {
                                                                        value: 100,
                                                                        message: "Item name must not exceed 100 characters"
                                                                    }
                                                                })}
                                                                onChange={(e) => {
                                                                    setValue(`lineItems.${index}.itemName`, e.target.value);
                                                                    if (e.target.value.length >= 3) {
                                                                        clearNestedFieldError("lineItems", "itemName", index);
                                                                    }
                                                                }}
                                                                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm p-2 ${
                                                                    getNestedErrorMessage("lineItems", "itemName", index)
                                                                        ? "border-red-500 focus:ring-red-500" 
                                                                        : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                                }`}
                                                                placeholder="Enter item name"
                                                            />
                                                            {getNestedErrorMessage("lineItems", "itemName", index) && (
                                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                    {getNestedErrorMessage("lineItems", "itemName", index)}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label
                                                                htmlFor={`lineItems.${index}.quantity`}
                                                                className="block text-xs font-medium text-gray-600"
                                                            >
                                                                Quantity <span className="text-red-500">*</span>
                                                            </label>
                                                            <input
                                                                type="number"
                                                                id={`lineItems.${index}.quantity`}
                                                                {...register(`lineItems.${index}.quantity`, {
                                                                    required: "Quantity is required",
                                                                    min: {
                                                                        value: 1,
                                                                        message: "Quantity must be at least 1"
                                                                    },
                                                                    max: {
                                                                        value: 9999,
                                                                        message: "Quantity must not exceed 9999"
                                                                    }
                                                                })}
                                                                onChange={(e) => {
                                                                    setValue(`lineItems.${index}.quantity`, e.target.value);
                                                                    if (e.target.value && parseFloat(e.target.value) >= 1) {
                                                                        clearNestedFieldError("lineItems", "quantity", index);
                                                                    }
                                                                }}
                                                                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm p-2 ${
                                                                    getNestedErrorMessage("lineItems", "quantity", index)
                                                                        ? "border-red-500 focus:ring-red-500" 
                                                                        : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                                }`}
                                                                placeholder="1"
                                                            />
                                                            {getNestedErrorMessage("lineItems", "quantity", index) && (
                                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                    {getNestedErrorMessage("lineItems", "quantity", index)}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label
                                                                htmlFor={`lineItems.${index}.unitPrice`}
                                                                className="block text-xs font-medium text-gray-600"
                                                            >
                                                                Unit Price
                                                            </label>
                                                            <input
                                                                type="number"
                                                                id={`lineItems.${index}.unitPrice`}
                                                                step="0.01"
                                                                {...register(`lineItems.${index}.unitPrice`, {
                                                                    min: {
                                                                        value: 0,
                                                                        message: "Price cannot be negative"
                                                                    },
                                                                    max: {
                                                                        value: 999999,
                                                                        message: "Price must not exceed 999999"
                                                                    }
                                                                })}
                                                                onChange={(e) => {
                                                                    setValue(`lineItems.${index}.unitPrice`, e.target.value);
                                                                    if (e.target.value !== undefined && parseFloat(e.target.value) >= 0) {
                                                                        clearNestedFieldError("lineItems", "unitPrice", index);
                                                                    }
                                                                }}
                                                                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm p-2 ${
                                                                    getNestedErrorMessage("lineItems", "unitPrice", index)
                                                                        ? "border-red-500 focus:ring-red-500" 
                                                                        : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                                }`}
                                                                placeholder="0.00"
                                                            />
                                                            {getNestedErrorMessage("lineItems", "unitPrice", index) && (
                                                                <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                    <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                    {getNestedErrorMessage("lineItems", "unitPrice", index)}
                                                                </p>
                                                            )}
                                                        </div>
                                                        {watchedLineItems?.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveLineItem(index)}
                                                                className="text-red-500 hover:text-red-700 text-sm mt-2"
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={handleAddLineItem}
                                                    className="mt-3 text-sm text-orange-600 hover:text-orange-800 flex items-center"
                                                >
                                                    <FiFileText className="mr-1" />
                                                    + Add Another Item
                                                </button>
                                            </div>
                                        )}

                                        {watchedTypeOfProcurement?.value === "financial_request" && (
                                            <div className="border border-dashed border-gray-300 p-4 rounded-md bg-gray-50">
                                                <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                                    <FiDollarSign className="mr-2 text-orange-500" />
                                                    Financial Details
                                                </h4>
                                                <div>
                                                    <label htmlFor="financialDetails.amount" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Amount <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <FiDollarSign className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="financialDetails.amount"
                                                            step="0.01"
                                                            {...register("financialDetails.amount", {
                                                                required: "Amount is required",
                                                                min: {
                                                                    value: 0.01,
                                                                    message: "Amount must be at least 0.01"
                                                                },
                                                                max: {
                                                                    value: 999999,
                                                                    message: "Amount must not exceed 999999"
                                                                }
                                                            })}
                                                            onChange={(e) => {
                                                                setValue("financialDetails.amount", e.target.value);
                                                                if (e.target.value && parseFloat(e.target.value) >= 0.01) {
                                                                    clearNestedFieldError("financialDetails", "amount");
                                                                }
                                                            }}
                                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${
                                                                getNestedErrorMessage("financialDetails", "amount")
                                                                    ? "border-red-500 focus:ring-red-500" 
                                                                    : "border-gray-300 focus:ring-orange-500"
                                                            } focus:outline-none focus:ring-2`}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    {getNestedErrorMessage("financialDetails", "amount") && (
                                                        <p className="mt-1 text-xs text-red-600 flex items-center">
                                                            <FiAlertCircle className="h-3 w-3 mr-1" />
                                                            {getNestedErrorMessage("financialDetails", "amount")}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="mt-3">
                                                    <label htmlFor="financialDetails.currency" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Currency <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="financialDetails.currency"
                                                        {...register("financialDetails.currency", {
                                                            required: "Currency is required",
                                                            minLength: {
                                                                value: 3,
                                                                message: "Currency must be 3 characters"
                                                            },
                                                            maxLength: {
                                                                value: 3,
                                                                message: "Currency must be 3 characters"
                                                            },
                                                            pattern: {
                                                                value: /^[A-Z]{3}$/,
                                                                message: "Currency must be 3 uppercase letters (e.g., USD)"
                                                            }
                                                        })}
                                                        onChange={(e) => {
                                                            setValue("financialDetails.currency", e.target.value);
                                                            if (/^[A-Z]{3}$/.test(e.target.value)) {
                                                                clearNestedFieldError("financialDetails", "currency");
                                                            }
                                                        }}
                                                        className={`w-full rounded-md shadow-sm sm:text-sm p-3 ${
                                                            getNestedErrorMessage("financialDetails", "currency")
                                                                ? "border-red-500 focus:ring-red-500" 
                                                                : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                        }`}
                                                        placeholder="USD"
                                                    />
                                                    {getNestedErrorMessage("financialDetails", "currency") && (
                                                        <p className="mt-1 text-xs text-red-600 flex items-center">
                                                            <FiAlertCircle className="h-3 w-3 mr-1" />
                                                            {getNestedErrorMessage("financialDetails", "currency")}
                                                        </p>
                                                    )}
                                                </div>

                                                
                                                <div className="mt-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Budget Code
                                                    </label>
                                                    
                                                    {displayBudgetCode ? (
                                                        <div className="flex items-center p-3 bg-orange-50 border border-orange-200 rounded-md">
                                                            <div className="p-2 bg-orange-100 rounded-full mr-3">
                                                                {/* Ensure FiTag is imported, or use FiDollarSign which is already imported */}
                                                                <FiDollarSign className="text-orange-600 h-5 w-5" /> 
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Funds Tagged To</p>
                                                                <p className="text-lg font-bold text-gray-800">{displayBudgetCode}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-400 text-sm italic">
                                                            Select a project to view the assigned budget code.
                                                        </div>
                                                    )}
                                                    
                                                    {/* Hidden input to keep form valid if needed, though backend handles the logic */}
                                                    <input type="hidden" {...register("financialDetails.budgetCode")} />
                                                </div>
                                            </div>
                                        )}

                                        {watchedTypeOfProcurement?.value === "inquiry_resolution" && (
                                            <div className="border border-dashed border-gray-300 p-4 rounded-md bg-gray-50">
                                                <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                                    <FiHelpCircle className="mr-2 text-orange-500" />
                                                    Inquiry Details
                                                </h4>
                                                <div>
                                                    <label
                                                        htmlFor="inquiryDetails.caseReference"
                                                        className="block text-sm font-medium text-gray-700 mb-2"
                                                    >
                                                        Case Reference <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="inquiryDetails.caseReference"
                                                        {...register("inquiryDetails.caseReference", { 
                                                            required: "Case reference is required",
                                                            minLength: {
                                                                value: 3,
                                                                message: "Case reference must be at least 3 characters"
                                                            },
                                                            maxLength: {
                                                                value: 50,
                                                                message: "Case reference must not exceed 50 characters"
                                                            }
                                                        })}
                                                        onChange={(e) => {
                                                            setValue("inquiryDetails.caseReference", e.target.value);
                                                            if (e.target.value.length >= 3) {
                                                                clearNestedFieldError("inquiryDetails", "caseReference");
                                                            }
                                                        }}
                                                        className={`w-full rounded-md shadow-sm sm:text-sm p-3 ${
                                                            getNestedErrorMessage("inquiryDetails", "caseReference")
                                                                ? "border-red-500 focus:ring-red-500" 
                                                                : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                        }`}
                                                        placeholder="Enter case reference"
                                                    />
                                                    {getNestedErrorMessage("inquiryDetails", "caseReference") && (
                                                        <p className="mt-1 text-xs text-red-600 flex items-center">
                                                            <FiAlertCircle className="h-3 w-3 mr-1" />
                                                            {getNestedErrorMessage("inquiryDetails", "caseReference")}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="mt-3">
                                                    <label
                                                        htmlFor="inquiryDetails.resolutionNotes"
                                                        className="block text-sm font-medium text-gray-700 mb-2"
                                                    >
                                                        Resolution Notes <span className="text-red-500">*</span>
                                                    </label>
                                                    <textarea
                                                        id="inquiryDetails.resolutionNotes"
                                                        {...register("inquiryDetails.resolutionNotes", { 
                                                            required: "Resolution notes are required",
                                                            minLength: {
                                                                value: 10,
                                                                message: "Resolution notes must be at least 10 characters"
                                                            },
                                                            maxLength: {
                                                                value: 1000,
                                                                message: "Resolution notes must not exceed 1000 characters"
                                                            }
                                                        })}
                                                        onChange={(e) => {
                                                            setValue("inquiryDetails.resolutionNotes", e.target.value);
                                                            if (e.target.value.length >= 10) {
                                                                clearNestedFieldError("inquiryDetails", "resolutionNotes");
                                                            }
                                                        }}
                                                        rows="3"
                                                        className={`w-full rounded-md shadow-sm sm:text-sm p-3 resize-none ${
                                                            getNestedErrorMessage("inquiryDetails", "resolutionNotes")
                                                                ? "border-red-500 focus:ring-red-500" 
                                                                : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                        }`}
                                                        placeholder="Enter resolution notes"
                                                    />
                                                    {getNestedErrorMessage("inquiryDetails", "resolutionNotes") && (
                                                        <p className="mt-1 text-xs text-red-600 flex items-center">
                                                            <FiAlertCircle className="h-3 w-3 mr-1" />
                                                            {getNestedErrorMessage("inquiryDetails", "resolutionNotes")}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {watchedTypeOfProcurement?.value === "travel" && (
                                            <div className="border border-dashed border-gray-300 p-4 rounded-md bg-gray-50">
                                                <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                                    <FiMapPin className="mr-2 text-orange-500" />
                                                    Travel Details
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label htmlFor="travelDetails.requesterName" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Requester Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                <FiUser className="h-5 w-5 text-gray-400" />
                                                            </div>
                                                            <input
                                                                type="text"
                                                                id="travelDetails.requesterName"
                                                                {...register("travelDetails.requesterName", { 
                                                                    required: "Requester name is required",
                                                                    minLength: {
                                                                        value: 3,
                                                                        message: "Name must be at least 3 characters"
                                                                    },
                                                                    maxLength: {
                                                                        value: 100,
                                                                        message: "Name must not exceed 100 characters"
                                                                    }
                                                                })}
                                                                onChange={(e) => {
                                                                    setValue("travelDetails.requesterName", e.target.value);
                                                                    if (e.target.value.length >= 3) {
                                                                        clearNestedFieldError("travelDetails", "requesterName");
                                                                    }
                                                                }}
                                                                className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${
                                                                    getNestedErrorMessage("travelDetails", "requesterName")
                                                                        ? "border-red-500 focus:ring-red-500" 
                                                                        : "border-gray-300 focus:ring-orange-500"
                                                                } focus:outline-none focus:ring-2`}
                                                                placeholder="Enter requester name"
                                                            />
                                                        </div>
                                                        {getNestedErrorMessage("travelDetails", "requesterName") && (
                                                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                {getNestedErrorMessage("travelDetails", "requesterName")}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.destination" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Destination <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                <FiMapPin className="h-5 w-5 text-gray-400" />
                                                            </div>
                                                            <input
                                                                type="text"
                                                                id="travelDetails.destination"
                                                                {...register("travelDetails.destination", { 
                                                                    required: "Destination is required",
                                                                    minLength: {
                                                                        value: 3,
                                                                        message: "Destination must be at least 3 characters"
                                                                    },
                                                                    maxLength: {
                                                                        value: 100,
                                                                        message: "Destination must not exceed 100 characters"
                                                                    }
                                                                })}
                                                                onChange={(e) => {
                                                                    setValue("travelDetails.destination", e.target.value);
                                                                    if (e.target.value.length >= 3) {
                                                                        clearNestedFieldError("travelDetails", "destination");
                                                                    }
                                                                }}
                                                                className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${
                                                                    getNestedErrorMessage("travelDetails", "destination")
                                                                        ? "border-red-500 focus:ring-red-500" 
                                                                        : "border-gray-300 focus:ring-orange-500"
                                                                } focus:outline-none focus:ring-2`}
                                                                placeholder="Enter destination"
                                                            />
                                                        </div>
                                                        {getNestedErrorMessage("travelDetails", "destination") && (
                                                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                {getNestedErrorMessage("travelDetails", "destination")}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.purpose" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Purpose of Travel <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="travelDetails.purpose"
                                                            {...register("travelDetails.purpose", { 
                                                                required: "Purpose is required",
                                                                minLength: {
                                                                    value: 5,
                                                                    message: "Purpose must be at least 5 characters"
                                                                },
                                                                maxLength: {
                                                                    value: 200,
                                                                    message: "Purpose must not exceed 200 characters"
                                                                }
                                                            })}
                                                            onChange={(e) => {
                                                                setValue("travelDetails.purpose", e.target.value);
                                                                if (e.target.value.length >= 5) {
                                                                    clearNestedFieldError("travelDetails", "purpose");
                                                                }
                                                            }}
                                                            className={`w-full rounded-md shadow-sm sm:text-sm p-3 ${
                                                                getNestedErrorMessage("travelDetails", "purpose")
                                                                    ? "border-red-500 focus:ring-red-500" 
                                                                    : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                            }`}
                                                            placeholder="Enter purpose of travel"
                                                        />
                                                        {getNestedErrorMessage("travelDetails", "purpose") && (
                                                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                {getNestedErrorMessage("travelDetails", "purpose")}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.startDate" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Start Date <span className="text-red-500">*</span>
                                                        </label>
                                                        <Controller
                                                            name="travelDetails.startDate"
                                                            control={control}
                                                            rules={{ 
                                                                required: "Start date is required",
                                                                validate: value => {
                                                                    if (!value) return "Start date is required";
                                                                    const startDate = new Date(value);
                                                                    const today = new Date();
                                                                    today.setHours(0, 0, 0, 0);
                                                                    if (startDate < today) {
                                                                        return "Start date cannot be in the past";
                                                                    }
                                                                    return true;
                                                                }
                                                            }}
                                                            render={({ field }) => (
                                                                <DatePicker
                                                                    selected={field.value}
                                                                    onChange={(date) => {
                                                                        field.onChange(date);
                                                                        if (date) {
                                                                            const startDate = new Date(date);
                                                                            const today = new Date();
                                                                            today.setHours(0, 0, 0, 0);
                                                                            if (startDate >= today) {
                                                                                clearNestedFieldError("travelDetails", "startDate");
                                                                            }
                                                                        }
                                                                    }}
                                                                    className={`w-full rounded-md shadow-sm sm:text-sm p-3 ${
                                                                        getNestedErrorMessage("travelDetails", "startDate")
                                                                            ? "border-red-500 focus:ring-red-500" 
                                                                            : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                                    }`}
                                                                    placeholderText="Select start date"
                                                                />
                                                            )}
                                                        />
                                                        {getNestedErrorMessage("travelDetails", "startDate") && (
                                                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                {getNestedErrorMessage("travelDetails", "startDate")}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.endDate" className="block text-sm font-medium text-gray-700 mb-2">
                                                            End Date <span className="text-red-500">*</span>
                                                        </label>
                                                        <Controller
                                                            name="travelDetails.endDate"
                                                            control={control}
                                                            rules={{ 
                                                                required: "End date is required",
                                                                validate: value => {
                                                                    if (!value) return "End date is required";
                                                                    const endDate = new Date(value);
                                                                    const startDate = watch("travelDetails.startDate");
                                                                    if (startDate && endDate < startDate) {
                                                                        return "End date must be after start date";
                                                                    }
                                                                    return true;
                                                                }
                                                            }}
                                                            render={({ field }) => (
                                                                <DatePicker
                                                                    selected={field.value}
                                                                    onChange={(date) => {
                                                                        field.onChange(date);
                                                                        if (date) {
                                                                            const endDate = new Date(date);
                                                                            const startDate = watch("travelDetails.startDate");
                                                                            if (startDate && endDate >= startDate) {
                                                                                clearNestedFieldError("travelDetails", "endDate");
                                                                            }
                                                                        }
                                                                    }}
                                                                    className={`w-full rounded-md shadow-sm sm:text-sm p-3 ${
                                                                        getNestedErrorMessage("travelDetails", "endDate")
                                                                            ? "border-red-500 focus:ring-red-500" 
                                                                            : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                                    }`}
                                                                    placeholderText="Select end date"
                                                                />
                                                            )}
                                                        />
                                                        {getNestedErrorMessage("travelDetails", "endDate") && (
                                                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                {getNestedErrorMessage("travelDetails", "endDate")}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.estimatedCost" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Estimated Cost <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                <FiDollarSign className="h-5 w-5 text-gray-400" />
                                                            </div>
                                                            <input
                                                                type="number"
                                                                id="travelDetails.estimatedCost"
                                                                step="0.01"
                                                                {...register("travelDetails.estimatedCost", { 
                                                                    required: "Estimated cost is required",
                                                                    min: {
                                                                        value: 0.01,
                                                                        message: "Cost must be at least 0.01"
                                                                    },
                                                                    max: {
                                                                        value: 999999,
                                                                        message: "Cost must not exceed 999999"
                                                                    }
                                                                })}
                                                                onChange={(e) => {
                                                                    setValue("travelDetails.estimatedCost", e.target.value);
                                                                    if (e.target.value && parseFloat(e.target.value) >= 0.01) {
                                                                        clearNestedFieldError("travelDetails", "estimatedCost");
                                                                    }
                                                                }}
                                                                className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${
                                                                    getNestedErrorMessage("travelDetails", "estimatedCost")
                                                                        ? "border-red-500 focus:ring-red-500" 
                                                                        : "border-gray-300 focus:ring-orange-500"
                                                                } focus:outline-none focus:ring-2`}
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                        {getNestedErrorMessage("travelDetails", "estimatedCost") && (
                                                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                                                <FiAlertCircle className="h-3 w-3 mr-1" />
                                                                {getNestedErrorMessage("travelDetails", "estimatedCost")}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.urgency" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Urgency
                                                        </label>
                                                        <Controller
                                                            name="travelDetails.urgency"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select
                                                                    {...field}
                                                                    options={URGENCY_OPTIONS}
                                                                    className="mt-1"
                                                                    placeholder="Select urgency"
                                                                    styles={{
                                                                        control: (base, state) => ({
                                                                            ...base,
                                                                            borderRadius: '0.75rem',
                                                                            minHeight: '44px',
                                                                            borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                                                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                            '&:hover': {
                                                                                borderColor: '#F97316'
                                                                            }
                                                                        }),
                                                                        option: (base, state) => ({
                                                                            ...base,
                                                                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                            color: state.isSelected ? 'white' : '#1F2937',
                                                                            '&:hover': {
                                                                                backgroundColor: '#FFEDD5'
                                                                            }
                                                                        }),
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.transportMode" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Mode of Transport
                                                        </label>
                                                        <Controller
                                                            name="travelDetails.transportMode"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select
                                                                    {...field}
                                                                    options={TRANSPORT_MODE_OPTIONS}
                                                                    className="mt-1"
                                                                    placeholder="Select transport mode"
                                                                    styles={{
                                                                        control: (base, state) => ({
                                                                            ...base,
                                                                            borderRadius: '0.75rem',
                                                                            minHeight: '44px',
                                                                            borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                                                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                            '&:hover': {
                                                                                borderColor: '#F97316'
                                                                            }
                                                                        }),
                                                                        option: (base, state) => ({
                                                                            ...base,
                                                                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                            color: state.isSelected ? 'white' : '#1F2937',
                                                                            '&:hover': {
                                                                                backgroundColor: '#FFEDD5'
                                                                            }
                                                                        }),
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label htmlFor="travelDetails.accommodationType" className="block text-sm font-medium text-gray-700 mb-2">
                                                            Accommodation
                                                        </label>
                                                        <Controller
                                                            name="travelDetails.accommodationType"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select
                                                                    {...field}
                                                                    options={ACCOMMODATION_TYPE_OPTIONS}
                                                                    className="mt-1"
                                                                    placeholder="Select accommodation"
                                                                    styles={{
                                                                        control: (base, state) => ({
                                                                            ...base,
                                                                            borderRadius: '0.75rem',
                                                                            minHeight: '44px',
                                                                            borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                                                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                                            '&:hover': {
                                                                                borderColor: '#F97316'
                                                                            }
                                                                        }),
                                                                        option: (base, state) => ({
                                                                            ...base,
                                                                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                                            color: state.isSelected ? 'white' : '#1F2937',
                                                                            '&:hover': {
                                                                                backgroundColor: '#FFEDD5'
                                                                            }
                                                                        }),
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-4">
                                                    <label htmlFor="travelDetails.description" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Additional Details
                                                    </label>
                                                    <textarea
                                                        id="travelDetails.description"
                                                        {...register("travelDetails.description", {
                                                            maxLength: {
                                                                value: 500,
                                                                message: "Additional details must not exceed 500 characters"
                                                            }
                                                        })}
                                                        onChange={(e) => {
                                                            setValue("travelDetails.description", e.target.value);
                                                            if (e.target.value.length <= 500) {
                                                                clearNestedFieldError("travelDetails", "description");
                                                            }
                                                        }}
                                                        rows="3"
                                                        className={`w-full rounded-md shadow-sm sm:text-sm p-3 resize-none ${
                                                            getNestedErrorMessage("travelDetails", "description")
                                                                ? "border-red-500 focus:ring-red-500" 
                                                                : "border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                                        }`}
                                                        placeholder="Enter additional details"
                                                    ></textarea>
                                                    {getNestedErrorMessage("travelDetails", "description") && (
                                                        <p className="mt-1 text-xs text-red-600 flex items-center">
                                                            <FiAlertCircle className="h-3 w-3 mr-1" />
                                                            {getNestedErrorMessage("travelDetails", "description")}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* File Attachment Section */}
                                        <div className="border border-dashed border-gray-300 p-4 rounded-md bg-gray-50">
                                            <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                                <FiUpload className="mr-2 text-orange-500" />
                                                Attachments
                                            </h4>
                                            <div>
                                                <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Select Files
                                                </label>
                                                <div className="flex items-center justify-center w-full">
                                                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                            <FiUpload className="w-8 h-8 mb-2 text-gray-400" />
                                                            <p className="text-sm text-gray-500">
                                                                <span className="font-semibold">Click to upload</span> or drag and drop
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                PDF, DOC, XLS, JPG, PNG up to 10MB
                                                            </p>
                                                        </div>
                                                        <input
                                                            type="file"
                                                            id="attachments"
                                                            multiple
                                                            onChange={onFileChange}
                                                            className="hidden"
                                                        />
                                                    </label>
                                                </div>
                                                {attachmentFiles.length > 0 && (
                                                    <div className="mt-3">
                                                        <p className="text-sm font-medium text-gray-700">Selected Files:</p>
                                                        <ul className="list-disc list-inside mt-1 text-sm text-gray-600">
                                                            {attachmentFiles.map((file, index) => (
                                                                <li key={index}>{file.name}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Form Submission Buttons */}
                                        <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                                            <button
                                                type="button"
                                                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200 order-2 sm:order-1"
                                                onClick={onClose}
                                                disabled={isSubmitting || isUploading}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center order-1 sm:order-2"
                                                disabled={isSubmitting || isUploading || isSubmitted}
                                            >
                                                {(isSubmitting || isUploading) && <FiLoader className="mr-2 h-4 w-4 animate-spin" />}
                                                {isEditing ? (isSubmitting || isUploading ? "Updating..." : "Update Requisition") : (isSubmitting || isUploading ? "Creating..." : "Create Requisition")}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default RequisitionFormModal